import * as React from 'react';
import {
  IWeightReportState,
  weightReportProvider,
} from '../../../data/providers/reports/weightReportProvider';
import { Entity, EntityType } from '../../../data/types';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../utils/ErrorMessages';
import { quartileColors } from '../utils/quartileColors';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { ResultList } from './ResultList';
import { WeightSettings } from './settings/Settings';
import { WeightGraphCard } from './WeightGraphCard';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const WeightReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<IWeightReportState>();
  /**
   * @description subscribe to data provider
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = weightReportProvider(
      props.entity,
      props.entityType
    ).subscribe((state: IWeightReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename('weights', props.entity, reportState?.timeframe);

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={(): void => {
                return null;
              }}
              filename={filename}
              title='Weight Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <Column>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.settings?.baselineTimeframe?.dateType,
                    dateRange:
                      reportState.settings?.baselineTimeframe?.dateRange,
                  }}
                  populationEntityName={reportState.entity?.name}
                  targetTimeframe={{
                    dateType: reportState?.settings?.timeframe?.dateType,
                    dateRange: reportState.settings?.timeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName='Weight Report'
                  isPdf={isPdf(props.pdfMode)}
                />
                {props.entityType === 'athletes' && (
                  <WeightGraphCard
                    entity={props.entity}
                    reportState={reportState}
                    pdfMode={props.pdfMode}
                    quartileColors={quartileColors}
                  />
                )}
                <ResultList
                  reportState={reportState}
                  colors={quartileColors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </Column>
            </Pdf>
          </PdfContainer>
        ) : (
          <Column>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.settings?.baselineTimeframe?.dateType,
                    dateRange:
                      reportState.settings?.baselineTimeframe?.dateRange,
                  }}
                  populationEntityName={reportState.entity?.name}
                  targetTimeframe={{
                    dateType: reportState?.settings?.timeframe?.dateType,
                    dateRange: reportState.settings?.timeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName='Weight Report'
                  isPdf={isPdf(props.pdfMode)}
                />
                {props.entityType === 'athletes' && (
                  <WeightGraphCard
                    entity={props.entity}
                    reportState={reportState}
                    pdfMode={props.pdfMode}
                    quartileColors={quartileColors}
                  />
                )}
                <ResultList
                  reportState={reportState}
                  colors={quartileColors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </Column>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-units':
      return (
        <ErrorMessage iconName='poll' text='Configure Weight Units'>
          <div style={{ marginTop: '10px' }}>
            Please select your desired metrics for each axis in settings.
          </div>
          <WeightSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'invalid-date-range':
      return (
        <ErrorMessage iconName='date_range' text='Missing Date Range'>
          <div style={{ marginTop: '10px' }}>
            Please select a date range for this report.
          </div>
          <WeightSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            There are not enough data points to produce this report. Try
            changing the timeframe in settings
          </div>
          <WeightSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
